import type { SubscriptionStatus } from "../types";

/**
 * 5c: label of the purchase button on /tenant/subscription. Keep in sync with
 * `TRIAL_PURCHASE_CTA_LABEL` in
 * `apps/backend/src/mail/templates/billing/expiration-notice.template.ts`
 * (the email tells the user to click this label).
 */
export const TRIAL_PURCHASE_CTA_LABEL = "Kupuję — wystaw proformę";

/**
 * 5c: whether the subscription is a trial without automatic proforma
 * (auto-renewal explicitly off, e.g. trial invite). The customer then buys
 * explicitly with "Kupuję — wystaw proformę" (POST /billing/self-service/trial-purchase).
 *
 * `autoRenewal` absent from the API means "on" (backward compat), so only an
 * explicit `false` qualifies.
 *
 * @see docs/specifications/2026-09-30-trial-invite-bez-proformy/spec.md § 5c
 */
export const isTrialWithoutAutoProforma = (
  s: Pick<SubscriptionStatus, "status" | "billingModel" | "autoRenewal"> | null | undefined
): boolean =>
  s?.status === "trialing" &&
  s.billingModel === "ordering" &&
  s.autoRenewal === false;

/**
 * Shown when the user may not buy: under the disabled "Kupuję" button and as
 * the error of a 403 from trial-purchase (e.g. role revoked meanwhile).
 */
export const TRIAL_PURCHASE_ADMIN_ONLY_MESSAGE =
  "Zakup może złożyć tylko administrator organizacji.";

/**
 * 5c: whether the user may click "Kupuję". POST /billing/self-service/trial-purchase
 * is behind TenantAdminGuard, which checks the user's role in the selected
 * tenant (`tenants[].role` from /auth/me) — anyone else gets a 403.
 */
export const canPurchaseTrial = (
  tenant: { role?: string } | null | undefined
): boolean => tenant?.role === "tenant_admin";
